import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  FormControlLabel,
  Switch,
  IconButton,
  InputAdornment,
  Chip
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  ContentCopy,
  Email
} from '@mui/icons-material';
import { getAuthToken } from '../../utils/authUtils';

const PasswordResetDialog = ({ 
  open, 
  onClose, 
  user, 
  onSuccess 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [useCustomPassword, setUseCustomPassword] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState('');

  const handleReset = () => {
    setError('');
    setSuccess('');
    setNewPassword('');
    setUseCustomPassword(false);
    setSendEmail(false);
    setTemporaryPassword('');
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handlePasswordReset = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = await getAuthToken();
      const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';

      const response = await fetch(`${API_BASE}/admin/users/${user.id}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...(useCustomPassword && newPassword ? { newPassword } : {}),
          sendEmail
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSuccess(result.message);
        
        if (result.temporaryPassword) {
          setTemporaryPassword(result.temporaryPassword);
        }

        // Call success callback after a delay
        setTimeout(() => {
          if (onSuccess) onSuccess();
        }, 2000);
      } else {
        setError(result.error || result.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      setError(error.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccess('Password copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy password:', err);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Reset Password for {user?.name}
        <Typography variant="body2" color="text.secondary">
          {user?.email}
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action will immediately change the user's password. The user will need to use the new password to sign in.
          </Alert>

          {/* Option Selection */}
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={sendEmail}
                  onChange={(e) => {
                    setSendEmail(e.target.checked);
                    if (e.target.checked) {
                      setUseCustomPassword(false);
                    }
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Email fontSize="small" />
                  Send password reset email
                </Box>
              }
            />
            <Typography variant="caption" display="block" color="text.secondary">
              User will receive an email to reset their password themselves
            </Typography>
          </Box>

          {!sendEmail && (
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={useCustomPassword}
                    onChange={(e) => setUseCustomPassword(e.target.checked)}
                  />
                }
                label="Set custom password"
              />
              <Typography variant="caption" display="block" color="text.secondary">
                {useCustomPassword 
                  ? "Enter a custom password for the user"
                  : "A secure random password will be generated"
                }
              </Typography>
            </Box>
          )}

          {/* Custom Password Input */}
          {useCustomPassword && !sendEmail && (
            <TextField
              fullWidth
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
          )}

          {/* Success/Error Messages */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {/* Generated Password Display */}
          {temporaryPassword && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Generated Temporary Password:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip 
                  label={temporaryPassword}
                  sx={{ 
                    fontFamily: 'monospace',
                    fontSize: '1rem',
                    height: 'auto',
                    py: 1
                  }}
                />
                <IconButton 
                  size="small" 
                  onClick={() => copyToClipboard(temporaryPassword)}
                  title="Copy to clipboard"
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Share this password securely with the user. They should change it after first login.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        <Button 
          onClick={handlePasswordReset}
          variant="contained"
          disabled={loading || (useCustomPassword && !newPassword)}
          color={sendEmail ? 'primary' : 'warning'}
        >
          {loading ? 'Processing...' : (
            sendEmail ? 'Send Reset Email' : 'Reset Password'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PasswordResetDialog; 